import type { ProjectTask } from "@/types/models";

export interface TaskDeletionBlocker {
  taskId: string;
  taskTitle: string;
  children: string[];
  blockedBy: string[];
  blocking: string[];
}

export interface TaskDeletionCandidate {
  id: string;
  title: string;
  type: ProjectTask["type"];
}

function uniqueNames(names: string[]) {
  return [...new Set(names.filter(Boolean))];
}

export function taskDeletionBlockers(
  tasks: readonly ProjectTask[],
  selectedTaskIds: readonly string[],
): TaskDeletionBlocker[] {
  const byId = new Map(tasks.map((task) => [task.id, task]));
  return selectedTaskIds.flatMap((taskId) => {
    const task = byId.get(taskId);
    if (!task) return [];
    const children = tasks
      .filter((candidate) => candidate.parentId === taskId)
      .map((candidate) => candidate.title);
    const blockedBy = task.predecessorIds.map(
      (predecessorId) => byId.get(predecessorId)?.title || "Unavailable task",
    );
    const blocking = tasks
      .filter((candidate) => candidate.predecessorIds.includes(taskId))
      .map((candidate) => candidate.title);
    if (!children.length && !blockedBy.length && !blocking.length) return [];
    return [
      {
        taskId,
        taskTitle: task.title,
        children: uniqueNames(children),
        blockedBy: uniqueNames(blockedBy),
        blocking: uniqueNames(blocking),
      },
    ];
  });
}

function stringList(value: unknown): string[] {
  return Array.isArray(value)
    ? uniqueNames(
        value.filter((item): item is string => typeof item === "string"),
      )
    : [];
}

export function taskDeletionBlockersFromDetails(
  details: Record<string, unknown> | undefined,
): TaskDeletionBlocker[] {
  const rows = details?.deletionBlockers;
  if (!Array.isArray(rows)) return [];
  return rows.flatMap((row) => {
    if (!row || typeof row !== "object") return [];
    const value = row as Record<string, unknown>;
    if (typeof value.taskId !== "string" || typeof value.taskTitle !== "string")
      return [];
    return [
      {
        taskId: value.taskId,
        taskTitle: value.taskTitle,
        children: stringList(value.children),
        blockedBy: stringList(value.blockedBy),
        blocking: stringList(value.blocking),
      },
    ];
  });
}

export function taskDeletionBlockedMessage(blockers: TaskDeletionBlocker[]) {
  const relatedCount = blockers.reduce(
    (count, blocker) =>
      count +
      blocker.children.length +
      blocker.blockedBy.length +
      blocker.blocking.length,
    0,
  );
  return `${blockers.length} selected item${blockers.length === 1 ? "" : "s"} ${blockers.length === 1 ? "has" : "have"} ${relatedCount} blocking relationship${relatedCount === 1 ? "" : "s"}.`;
}
